import {
	Footer,
	FooterAlt,
	FooterLite,
	Header,
	HeaderAspen,
	HeaderClearChoice2,
} from '@aspendental/shared-components-web';
import { IBanner } from '@aspendental/shared-components-web/lib/components/Banner/Banner';
import { IGoogleMap } from '@aspendental/shared-components-web/lib/components/GoogleMap/GoogleMap';
import { PATIENT_PRISM_BASE_PHONE } from '@aspendental/web-shared-utils';
import { ReactNode } from 'react';

import { AdFooter, App, Maybe, NavigationLink, Page } from '@src/lib/__generated/sdk';

interface LayoutPropsInterface {
	children: ReactNode;
	appData: Maybe<App>;
	footerData: Maybe<AdFooter>;
	isFooterLite?: boolean;
	isFooterAlt?: boolean;
	withSimpleLayout?: boolean;
	locale: string;
	preview: boolean;
	showHeaderNavMenu: boolean;
	breadcrumbsData: Page;
	locationNavigationLinks?: Array<NavigationLink | Page> | undefined;
	withFixedHeader: boolean;
	disableStickyFooterOnDesktop: boolean | null;
	disableStickyFooterOnMobile: boolean | null;
	disableHeaderTopAlertBanner: boolean | null;
	disableHeaderBottomAlertBanner: boolean | null;
	hideLocation?: boolean;
	hidePrimaryActionButton?: boolean;
	showAspen2Header?: boolean;
	showClearChoice2Header?: boolean;
	/**
	 * VWO `ccPatientPrismPhoneSwap` treatment. When true, the national ClearChoice
	 * header CTA renders the Patient Prism pool number (`PATIENT_PRISM_BASE_PHONE`)
	 * so Patient Prism can swap it for a dynamic tracking number. Facility/office
	 * numbers are never affected.
	 */
	phoneSwapOn?: boolean;
	/**
	 * VWO `ghostPrimaryScheduleButton` (TTB-322) treatment: render the ClearChoice2
	 * header's primary CTA as a lower-emphasis "ghost" button. Resolved server-side
	 * (`serverFeatureFlags`) in _app.page.tsx; defaults to the standard CTA.
	 */
	ghostPrimaryScheduleButton?: boolean;
}

function getFooter(
	footerData: Maybe<AdFooter>,
	isFooterLite: boolean,
	isFooterAlt: boolean,
	withSimpleLayout: boolean,
	breadcrumbsData: Page | undefined,
	disableStickyFooterOnDesktop: boolean | null,
	disableStickyFooterOnMobile: boolean | null,
	phoneSwapOn: boolean
) {
	if (isFooterLite) {
		return (
			<FooterLite
				disclaimer={footerData?.disclaimer as any}
				footerData={footerData as any}
				breadcrumbsData={breadcrumbsData as any}
			/>
		);
	} else if (isFooterAlt) {
		return <FooterAlt footerData={footerData as any} />;
	}
	return (
		<Footer
			footerData={footerData as any}
			withScheduling={withSimpleLayout}
			breadcrumbsData={breadcrumbsData as any}
			isLightFooter={!!footerData?.isLightFooter}
			disableStickyFooterOnMobile={disableStickyFooterOnMobile}
			disableStickyFooterOnDesktop={disableStickyFooterOnDesktop}
			phoneSwapNumber={phoneSwapOn ? PATIENT_PRISM_BASE_PHONE : undefined}
		/>
	);
}

export const Layout = ({
	appData,
	footerData,
	isFooterLite = true,
	isFooterAlt = false,
	withSimpleLayout = false,
	children,
	showHeaderNavMenu,
	breadcrumbsData,
	locationNavigationLinks,
	withFixedHeader,
	disableStickyFooterOnDesktop,
	disableStickyFooterOnMobile,
	disableHeaderTopAlertBanner,
	disableHeaderBottomAlertBanner,
	hideLocation,
	hidePrimaryActionButton = false,
	showAspen2Header = false,
	showClearChoice2Header = false,
	phoneSwapOn = false,
	ghostPrimaryScheduleButton = false,
}: LayoutPropsInterface) => {
	const renderedHeader = showAspen2Header ? (
		<HeaderAspen headerData={appData?.header as any} logo={appData?.logo as any} />
	) : showClearChoice2Header ? (
		<HeaderClearChoice2
			headerData={appData?.header as any}
			logo={appData?.logo as any}
			topAlertBanner={appData?.header?.topAlertBanner as IBanner}
			bottomAlertBanner={appData?.header?.bottomAlertBanner as IBanner}
			disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
			disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
			phoneSwapNumber={phoneSwapOn ? PATIENT_PRISM_BASE_PHONE : undefined}
			ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
		/>
	) : (
		<Header
			locationNavigationLinks={locationNavigationLinks as any}
			appData={appData as any}
			withOfficeDetails
			showHeaderNavMenu={showHeaderNavMenu}
			phoneNumber={appData?.header?.phoneNumber as any}
			withFixedHeader={withFixedHeader}
			useHeaderLocationCards={appData?.header?.useHeaderLocationCards}
			googleMapsForLocationCards={appData?.header?.googleMapsForLocationCards as IGoogleMap}
			hideLocation={hideLocation}
			hidePrimaryActionButton={hidePrimaryActionButton}
			topAlertBanner={appData?.header?.topAlertBanner as IBanner}
			bottomAlertBanner={appData?.header?.bottomAlertBanner as IBanner}
			disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
			disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
		/>
	);

	return (
		<>
			{appData && <>{renderedHeader}</>}
			{children}
			{footerData &&
				getFooter(
					footerData,
					isFooterLite,
					isFooterAlt,
					withSimpleLayout,
					breadcrumbsData,
					disableStickyFooterOnDesktop,
					disableStickyFooterOnMobile,
					phoneSwapOn
				)}
		</>
	);
};
